import { action } from 'mobx';
import { IFlowGalleryTemplate } from '~/components/FlowGallery/types/flow-gallery.types';
import type { RootStore } from '~/stores/root.store';
import { IFolder } from '~/types/model.types';
import {
    FlowPanel,
    ICreateFlowData,
    TFetchCompanyFlows,
    TFlowActionType,
    TFlowPanel,
    TProjectListFilters,
    TRequestStatus
} from '../types/create-flow.types';
import { trackError } from '~/services/logger/error.utils';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';

export class CreateFlowController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    setLayoutViewMode(val: boolean) {
        this.rootStore.createFlowUiStore.setLayoutViewMode(val);
    }

    setRequestStatus(val: TRequestStatus) {
        this.rootStore.createFlowUiStore.setRequestStatus(val);
    }

    loadFolders(folders: IFolder[]) {
        this.rootStore.createFlowUiStore.loadFolders(folders);
    }

    async fetchSuggestedTemplates() {
        const {
            createFlowUiStore,
            flowGalleryService,
            companyStore: { currCompany }
        } = this.rootStore;
        const {
            flowFilterTypes: { activeFilters }
        } = createFlowUiStore;

        try {
            const res = await flowGalleryService.listFlows({
                industryTags: [currCompany?.company_type || 'other'],
                actionTags: activeFilters
            });

            createFlowUiStore.loadSuggestedTemplates(res);
        } catch (error) {
            trackError({
                error,
                message: 'An error on Template Gallery fetchSuggestedTemplates'
            });
            createFlowUiStore.loadSuggestedTemplates([]);
        }
    }

    async fetchMostUsedTemplates(limit?: number) {
        const { templatesService, createFlowUiStore } = this.rootStore;
        const {
            flowFilterTypes: { activeFilters }
        } = createFlowUiStore;

        try {
            const res = await templatesService.fetchMostUsedTemplates({
                limit,
                activeFilters
            });

            createFlowUiStore.loadMostUsedTemplates(res);
        } catch {
            createFlowUiStore.loadMostUsedTemplates([]);
        }
    }

    async fetchRecentlySentFlows(limit?: number) {
        const { templatesService, createFlowUiStore } = this.rootStore;
        const {
            flowFilterTypes: { activeFilters }
        } = createFlowUiStore;

        try {
            const res = await templatesService.fetchRecentlySentFlows(
                limit,
                activeFilters
            );
            createFlowUiStore.loadRecentlySentFlows(res.flows);
            createFlowUiStore.loadRecentlySentLegacyFiles(res.files);
        } catch {
            createFlowUiStore.loadRecentlySentFlows([]);
            createFlowUiStore.loadRecentlySentLegacyFiles([]);
        }
    }

    async createFlowInstance(templateId?: string): Promise<any> {
        const { templatesService, createFlowUiStore } = this.rootStore;
        this.setRequestStatus('creating');
        /* NOTE: if !templateId creates blank flow */
        try {
            const res = await templatesService.createFlowInstance({
                templateId,
                workspaceId: createFlowUiStore.workspaceId
            });
            this.setRequestStatus('succeeded');
            return res;
        } catch (err) {
            this.setRequestStatus('failed');
            throw new Error(err);
        }
    }

    createFlowInstanceFromServices(templateId: string): Promise<any> {
        const {
            templatesService,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        try {
            return templatesService.createFlowInstanceFromServices(
                templateId,
                selectedFlow._id
            );
        } catch (err) {
            throw new Error(err);
        }
    }

    async createFlowFromLegacyFile(fileId: string): Promise<any> {
        const { templatesService, createFlowUiStore } = this.rootStore;
        this.setRequestStatus('creating');
        /* NOTE: if !templateId creates blank flow */
        try {
            const res = await templatesService.createFlowFromLegacyFile(
                fileId,
                createFlowUiStore.workspaceId
            );
            this.setRequestStatus('succeeded');
            return res;
        } catch (err) {
            this.setRequestStatus('failed');
            throw new Error(err);
        }
    }

    async createFlowInstanceFromGallery(
        templateId: string,
        publishedTemplateId: string,
        templateTitle: string
    ) {
        const {
            templatesService,
            flowGalleryService,
            createFlowUiStore: { workspaceId }
        } = this.rootStore;

        try {
            flowGalleryService
                .downloadFlow({
                    flowId: templateId
                })
                .then(galleryFlow => {
                    if (!galleryFlow) {
                        trackError({
                            error: new Error(
                                'Empty gallery flow returned from flow gallery service downloadFlow'
                            )
                        });
                    }
                });

            const importedTemplate = await templatesService.downloadTemplate(
                publishedTemplateId,
                templateTitle
            );
            return await templatesService.createFlowInstance({
                templateId: importedTemplate._id,
                workspaceId
            });
        } catch (err) {
            throw new Error(err);
        }
    }
    setOnCloseModal(closeFunc: Function) {
        const { createFlowUiStore } = this.rootStore;
        createFlowUiStore.setOnCloseModal(closeFunc);
    }

    setProjectListFilters(projectListFilters: TProjectListFilters) {
        const { createFlowUiStore } = this.rootStore;
        createFlowUiStore.setProjectListFilters(projectListFilters);
    }

    loadFlowActionTypes(actions: TFlowActionType[]) {
        const { createFlowUiStore } = this.rootStore;
        createFlowUiStore.setInitialFilters(actions);
        createFlowUiStore.loadFlowFilterTypes(actions);
    }

    async fetchCompanyFlows() {
        const {
            templatesService,
            createFlowUiStore,
            companyStore: { currCompany }
        } = this.rootStore;

        const {
            activePanel: { isStarred, isFolder },
            flowFilterTypes: { activeFilters },
            activeFolderPanel
        } = createFlowUiStore;

        const params: TFetchCompanyFlows = {
            companyId: currCompany._id,
            filters: activeFilters,
            flow_type: 'template',
            folder_id: null
        };

        if (isStarred) {
            params.filters = [...activeFilters, 'starred'];
        }

        if (isFolder) {
            params.folder_id = activeFolderPanel._id;
        }

        this.setRequestStatus('fetching');

        try {
            const res = await templatesService.fetchCompanyFlows(params);
            createFlowUiStore.loadFlowTemplates(res);
            this.setRequestStatus('succeeded');
        } catch {
            createFlowUiStore.loadFlowTemplates([]);
            this.setRequestStatus('failed');
        }
    }

    async fetchTemplates() {
        const { activeFlowPanel } = this.rootStore.createFlowUiStore;
        this.setRequestStatus('fetching');

        try {
            switch (activeFlowPanel) {
                case FlowPanel.Most_used_templates:
                    await Promise.allSettled([
                        this.fetchMostUsedTemplates(
                            this.rootStore.createFlowUiStore.maxFlowsToDisplay
                        )
                    ]);
                    break;
                case FlowPanel.All_flow_templates:
                case FlowPanel.Starred_templates:
                case FlowPanel.My_flows:
                    await this.fetchCompanyFlows();
                    break;
                case FlowPanel.Recently_sent_flows:
                    await this.fetchRecentlySentFlows(
                        this.rootStore.createFlowUiStore.maxFlowsToDisplay
                    );
                    break;
                case FlowPanel.Suggested_templates:
                    await this.fetchSuggestedTemplates();
                    break;
                default:
                    throw new Error('invalid query');
            }
            this.setRequestStatus('succeeded');
        } catch (error) {
            this.setRequestStatus('failed');
            trackError({
                error: error,
                context: {
                    source: 'Fail fetch templates for create flow modal',
                    user: this.rootStore.userStore.currUser._id
                }
            });
        }
    }

    setActiveFlowPanel(panel: TFlowPanel) {
        this.rootStore.createFlowUiStore.setActiveFlowPanel(panel);
        this.fetchTemplates();
    }

    setActiveFolderPanel(folder: IFolder) {
        this.rootStore.createFlowUiStore.setActiveFolderPanel(folder);
        this.fetchCompanyFlows();
    }

    setFlowFilterType(val: TFlowActionType) {
        const { createFlowUiStore } = this.rootStore;
        createFlowUiStore.setFlowFilterType(val);
        if (createFlowUiStore.activeFlowPanel) {
            this.fetchTemplates();
        } else {
            this.fetchCompanyFlows();
        }
    }

    @action.bound
    openQuickPreview(template: IFlowGalleryTemplate) {
        const {
            createFlowUiStore: { setIsQuickPreviewOpen },
            quickPreviewUiStore: { loadQuickPreviewById }
        } = this.rootStore;

        loadQuickPreviewById(template._id, [template]);
        setIsQuickPreviewOpen(true);
    }

    @action.bound
    closeQuickPreview() {
        const {
            createFlowUiStore: { setIsQuickPreviewOpen },
            quickPreviewUiStore: { resetQuickPreview }
        } = this.rootStore;

        setIsQuickPreviewOpen(false);
        resetQuickPreview();
    }

    /* Initialization */
    async loadCompany() {
        const {
            sessionStore,
            companyCtrl,
            companyStore: { currCompany }
        } = this.rootStore;
        await companyCtrl.fetchCompany(sessionStore.currCompanyId);
        this.loadFolders(currCompany.folders);
    }

    load(createFlowData: ICreateFlowData, workspace_id?: string) {
        const data: ICreateFlowData = createFlowData;
        this.loadCompany();
        this.loadFlowActionTypes(data.filters || []);
        this.fetchTemplates();
        this.setOnCloseModal(data.onClose);

        if (workspace_id && data.project_id) {
            /* NOTE: Must verify workspace_id && project_id 
            to confirm WS instance. */
            data.workspace_id = workspace_id;
            this.rootStore.createFlowUiStore.setProject(data);
        } else {
            this.rootStore.createFlowUiStore.setClient(data.clientId);
            this.setProjectListFilters(data.projectListFilters);
            this.rootStore.createFlowUiStore.setNavBarScoped(
                data.isNavBarScoped
            );
        }

        let appPath;

        if (!!(workspace_id && data.project_id)) {
            appPath = 'workspace';
        } else if (data.isNavBarScoped) {
            appPath = 'top nav';
        } else {
            appPath = data.appPath || 'client workspace';
        }

        this.rootStore.analyticsService.track(AnalyticsEvents.CreateFlow, {
            action: 'load',
            activeFilters:
                this.rootStore.createFlowUiStore.flowFilterTypes.activeFilters.toString(),
            app_path: appPath,
            source: data.source || appPath,
            ...(data.entryPoint ? { entry_point: data.entryPoint } : {})
        });

        this.rootStore.createFlowUiStore.setSource(data.source);
        if (data.entryPoint) {
            this.rootStore.createFlowUiStore.setEntryPoint(data.entryPoint);
        }
    }

    reset() {
        this.rootStore.createFlowUiStore.reset();
        this.rootStore.quickPreviewUiStore.resetQuickPreview();
    }
}
